import React, { useState } from 'react';
import { Save, Download, Eye, EyeOff, RefreshCw, Sparkles } from 'lucide-react';

// Comprehensive template library
const TEMPLATE_LIBRARY = {
  mathematics: {
    name: "Mathematics",
    color: "from-blue-600 to-blue-700",
    templates: [
      {
        id: "math-standard",
        name: "Standard Math Lesson",
        description: "Clean layout with ample workspace, theorem boxes, and step-by-step solutions",
        features: ["Large margins for work", "Numbered theorems", "Example/solution pairs", "Practice problems with space"]
      },
      {
        id: "math-graphing",
        name: "Graphing & Visualization",
        description: "Heavy TikZ integration with coordinate planes and function graphs",
        features: ["Pre-drawn coordinate systems", "Function plotting areas", "Transformation grids", "Graph analysis questions"]
      },
      {
        id: "math-geometry",
        name: "Geometry Proofs",
        description: "Diagram-focused layout with proof structures",
        features: ["Large diagram spaces", "Two-column proof templates", "Construction steps", "Angle/side labeling guides"]
      },
      {
        id: "math-calculus",
        name: "Calculus Workbook",
        description: "Derivative/integral focused with marginal note spaces",
        features: ["Formula reference boxes", "Limit notation", "Integration workspace", "Application problems"]
      },
      {
        id: "math-problem-set",
        name: "Problem Set",
        description: "Exam-style layout with graduated difficulty",
        features: ["Numbered problems", "Point values", "Workspace sections", "Bonus challenges"]
      },
      {
        id: "math-discovery",
        name: "Discovery Activities",
        description: "Guided exploration with tables and observation questions",
        features: ["Data tables", "Pattern recognition", "Conjecture prompts", "Verification steps"]
      }
    ]
  },
  language: {
    name: "Language Arts",
    color: "from-purple-600 to-purple-700",
    templates: [
      {
        id: "lang-vocabulary",
        name: "Vocabulary Builder",
        description: "Table-heavy design for word study and definitions",
        features: ["Word/definition tables", "Example sentence blanks", "Synonym/antonym columns", "Usage contexts"]
      },
      {
        id: "lang-grammar",
        name: "Grammar Practice",
        description: "Sentence manipulation and parts of speech identification",
        features: ["Sentence diagrams", "Fill-in-the-blank", "Error correction", "Transformation exercises"]
      },
      {
        id: "lang-reading-comp",
        name: "Reading Comprehension",
        description: "Passage with analysis questions and text evidence boxes",
        features: ["Formatted passages", "Margin annotation space", "Quote citation boxes", "Discussion questions"]
      },
      {
        id: "lang-writing-workshop",
        name: "Writing Workshop",
        description: "Scaffolded writing with brainstorm spaces and rubrics",
        features: ["Prewriting organizers", "Draft sections", "Peer review checklist", "Revision guides"]
      },
      {
        id: "lang-poetry",
        name: "Poetry Analysis",
        description: "Structured annotation and literary device identification",
        features: ["Line-numbered poems", "Device tracking tables", "Interpretation questions", "Creative response space"]
      },
      {
        id: "lang-spelling",
        name: "Spelling & Phonics",
        description: "Word lists with practice activities",
        features: ["Word lists", "Sound pattern tables", "Sentence integration", "Word sorts"]
      }
    ]
  },
  science: {
    name: "Science",
    color: "from-green-600 to-green-700",
    templates: [
      {
        id: "sci-lab-report",
        name: "Lab Report",
        description: "Complete scientific method structure with data tables",
        features: ["Hypothesis section", "Procedure steps", "Data tables", "Analysis questions", "Conclusion framework"]
      },
      {
        id: "sci-observation",
        name: "Observation & Data",
        description: "Heavy table layout for experimental data collection",
        features: ["Multi-column data tables", "Graph paper sections", "Observation checklists", "Variable tracking"]
      },
      {
        id: "sci-diagrams",
        name: "Diagram Labeling",
        description: "Large diagram spaces with label lines",
        features: ["Unlabeled diagrams", "Label banks", "Function descriptions", "Process arrows"]
      },
      {
        id: "sci-inquiry",
        name: "Scientific Inquiry",
        description: "Question-driven investigation structure",
        features: ["Research questions", "Evidence gathering", "Claim-Evidence-Reasoning", "Further investigation"]
      },
      {
        id: "sci-concepts",
        name: "Concept Notes",
        description: "Cornell-style notes with vocabulary and summaries",
        features: ["Two-column layout", "Key terms boxes", "Summary sections", "Visual organizers"]
      },
      {
        id: "sci-engineering",
        name: "Engineering Design",
        description: "Design challenge with constraints and iterations",
        features: ["Challenge statement", "Constraint boxes", "Sketch areas", "Testing tables", "Reflection"]
      }
    ]
  },
  socialStudies: {
    name: "Social Studies",
    color: "from-amber-600 to-amber-700",
    templates: [
      {
        id: "social-timeline",
        name: "Timeline Analysis",
        description: "Visual timeline with event descriptions",
        features: ["Horizontal timeline", "Event boxes", "Cause/effect chains", "Analysis questions"]
      },
      {
        id: "social-comparison",
        name: "Comparative Analysis",
        description: "Side-by-side comparison tables",
        features: ["Comparison matrices", "Venn diagrams", "Pros/cons tables", "Synthesis questions"]
      },
      {
        id: "social-primary-source",
        name: "Primary Source Analysis",
        description: "Document analysis with sourcing questions",
        features: ["Document excerpts", "Sourcing framework", "Contextualization", "Corroboration"]
      },
      {
        id: "social-geography",
        name: "Geography & Maps",
        description: "Map-based activities with legends",
        features: ["Blank maps", "Legend creation", "Scale activities", "Location questions"]
      },
      {
        id: "social-dbq",
        name: "Document-Based Questions",
        description: "Multiple documents with essay scaffold",
        features: ["Document collection", "Evidence charts", "Thesis builder", "Essay outline"]
      },
      {
        id: "social-biography",
        name: "Historical Figures",
        description: "Person-focused study guide",
        features: ["Bio fact boxes", "Impact analysis", "Quote examination", "Legacy discussion"]
      }
    ]
  },
  arts: {
    name: "Arts & Music",
    color: "from-pink-600 to-pink-700",
    templates: [
      {
        id: "art-critique",
        name: "Art Critique",
        description: "Structured art analysis framework",
        features: ["Image space", "Element analysis", "Interpretation guide", "Personal response"]
      },
      {
        id: "art-studio",
        name: "Studio Worksheet",
        description: "Project planning and reflection",
        features: ["Sketch space", "Material list", "Process steps", "Artist statement"]
      },
      {
        id: "music-theory",
        name: "Music Theory",
        description: "Staff paper with theory exercises",
        features: ["Music staves", "Note identification", "Rhythm exercises", "Interval practice"]
      },
      {
        id: "music-listening",
        name: "Listening Analysis",
        description: "Musical element tracking guide",
        features: ["Element checklist", "Timing notes", "Mood descriptors", "Comparison questions"]
      },
      {
        id: "drama-script",
        name: "Drama & Performance",
        description: "Script analysis and character study",
        features: ["Scene excerpts", "Character tables", "Staging notes", "Line interpretation"]
      }
    ]
  },
  assessment: {
    name: "Assessments",
    color: "from-red-600 to-red-700",
    templates: [
      {
        id: "test-standard",
        name: "Standard Test",
        description: "Clean exam layout with multiple question types",
        features: ["Header with name/date", "Multiple choice", "Short answer", "Point values", "Clear sections"]
      },
      {
        id: "test-open-ended",
        name: "Essay Test",
        description: "Long-form response format",
        features: ["Question prompts", "Lined answer spaces", "Rubric reference", "Planning area"]
      },
      {
        id: "quiz-quick",
        name: "Quick Quiz",
        description: "Brief assessment format",
        features: ["Compact layout", "10-15 questions", "Mixed formats", "Fast grading"]
      },
      {
        id: "rubric-detailed",
        name: "Detailed Rubric",
        description: "Comprehensive grading matrix",
        features: ["Criteria rows", "Performance levels", "Point distribution", "Comments section"]
      },
      {
        id: "checklist",
        name: "Skills Checklist",
        description: "Observable skills tracking",
        features: ["Skill list", "Proficiency levels", "Date columns", "Notes area"]
      },
      {
        id: "self-assessment",
        name: "Student Self-Assessment",
        description: "Reflection and goal-setting guide",
        features: ["Strength/growth areas", "Goal setting", "Evidence gathering", "Next steps"]
      }
    ]
  },
  organization: {
    name: "Organization & Planning",
    color: "from-indigo-600 to-indigo-700",
    templates: [
      {
        id: "unit-planner",
        name: "Unit Planning Guide",
        description: "Comprehensive unit overview",
        features: ["Learning goals", "Day-by-day breakdown", "Assessment plan", "Materials list"]
      },
      {
        id: "lesson-plan",
        name: "Lesson Plan Template",
        description: "Detailed single-lesson structure",
        features: ["Objectives", "Hook/intro", "Activities", "Closure", "Differentiation notes"]
      },
      {
        id: "calendar",
        name: "Class Calendar",
        description: "Monthly/weekly overview",
        features: ["Calendar grid", "Assignment tracking", "Event highlights", "Notes section"]
      },
      {
        id: "student-tracker",
        name: "Student Progress Tracker",
        description: "Individual student monitoring",
        features: ["Student roster", "Skill/standard columns", "Date markers", "Intervention notes"]
      },
      {
        id: "parent-communication",
        name: "Parent Communication",
        description: "Professional parent correspondence",
        features: ["Letterhead", "Formal structure", "Signature lines", "Tear-off response"]
      },
      {
        id: "behavior-log",
        name: "Behavior Tracking Log",
        description: "Incident and positive behavior recording",
        features: ["Date/time stamps", "Behavior descriptions", "Response actions", "Trend analysis"]
      }
    ]
  },
  special: {
    name: "Specialized",
    color: "from-cyan-600 to-cyan-700",
    templates: [
      {
        id: "graphic-organizer",
        name: "Graphic Organizers",
        description: "Visual thinking tools collection",
        features: ["Concept maps", "Flow charts", "Venn diagrams", "KWL charts", "Cause/effect"]
      },
      {
        id: "cornell-notes",
        name: "Cornell Notes",
        description: "Structured note-taking system",
        features: ["Three-section layout", "Cue column", "Notes column", "Summary section"]
      },
      {
        id: "flashcards",
        name: "Study Flashcards",
        description: "Printable flashcard set",
        features: ["Front/back format", "Perforated lines", "Category headers", "Multiple per page"]
      },
      {
        id: "research-guide",
        name: "Research Guide",
        description: "Step-by-step research project support",
        features: ["Topic selection", "Source tracking", "Note organization", "Citation help", "Outline builder"]
      },
      {
        id: "presentation-notes",
        name: "Presentation Notes",
        description: "Speaker notes and slide planning",
        features: ["Slide thumbnails", "Talking points", "Timing notes", "Visual cues"]
      },
      {
        id: "newsletter",
        name: "Class Newsletter",
        description: "Professional classroom newsletter",
        features: ["Multi-column", "Image spaces", "Highlights sections", "Calendar insert"]
      }
    ]
  }
};

const Lessons = () => {
  const [selectedCategory, setSelectedCategory] = useState('mathematics');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [lessonParams, setLessonParams] = useState({
    subject: '',
    gradeLevel: '',
    topic: '',
    duration: '',
    freeFormInstruction: '',
    includeHomework: false,
    homeworkCount: 5,
    includeSolutions: false
  });
  
  const [previewVisible, setPreviewVisible] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [lessonTitle, setLessonTitle] = useState('');
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setLessonParams({
      ...lessonParams,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const selectTemplate = (categoryKey, template) => {
    setSelectedTemplate({ category: categoryKey, ...template });
  };
  
  const generateLesson = async () => {
    if (!selectedTemplate) {
      alert('Please select a template first!');
      return;
    }
    
    setIsGenerating(true);
    setLessonTitle(`${lessonParams.topic} - ${selectedTemplate.name}`);
    
    try {
      const prompt = `
You are a world-class curriculum developer creating a beautiful LaTeX document using the "${selectedTemplate.name}" template.

OUTPUT REQUIREMENTS:
- Return a complete, compilable LaTeX document
- Begin with:
  \\documentclass{article}
  \\usepackage[utf8]{inputenc}
  \\usepackage{amsmath,amssymb}
  \\usepackage{geometry}
  \\usepackage{tikz}
  \\usepackage{pgfplots}
  \\usepackage{multicol}
  \\usepackage{tabularx}
  \\usepackage{booktabs}
  \\usepackage{enumitem}
  \\usepackage{fancyhdr}
  \\usepackage{graphicx}
  \\usepackage{tcolorbox}
  \\geometry{margin=1in}
  \\pgfplotsset{compat=1.18}
  \\begin{document}
- End with: \\end{document}
- NO markdown, NO backticks, ONLY LaTeX code

TEMPLATE: ${selectedTemplate.name}
DESCRIPTION: ${selectedTemplate.description}
KEY FEATURES: ${selectedTemplate.features.join(', ')}

CORE CONTEXT:
- Subject: ${lessonParams.subject}
- Grade Level: ${lessonParams.gradeLevel}
- Topic: ${lessonParams.topic}
- Duration: ${lessonParams.duration || 'Not specified'}

CUSTOM INSTRUCTIONS:
${lessonParams.freeFormInstruction}

${lessonParams.includeHomework ? `INCLUDE ${lessonParams.homeworkCount} homework/practice problems at the end` : ''}
${lessonParams.includeSolutions ? 'INCLUDE a complete solutions manual section' : ''}

CRITICAL DESIGN REQUIREMENTS:
- Follow the exact template style: ${selectedTemplate.name}
- Use the features listed: ${selectedTemplate.features.join(', ')}
- Make it visually stunning with proper spacing, boxes, and formatting
- Use \\section, \\subsection for clear structure
- Include ample white space and working room
- Use tcolorbox for important definitions/theorems
- Use professional fonts and spacing
- Add page breaks between major sections when appropriate
- Use geometry package for optimal margins

Make this document absolutely beautiful and professionally formatted!
`;

      const response = await fetch(`${process.env.REACT_APP_API_URL}/lessons/openAi`, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: prompt,
      });

      if (!response.ok) {
        throw new Error(`API call failed with status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      
    } catch (error) {
      console.error('Error generating lesson:', error);
      setPdfUrl(null);
      alert(`Error generating PDF: ${error.message}\n\nPlease check if the API server is running at ${process.env.REACT_APP_API_URL}`);
    } finally {
      setIsGenerating(false);
    }
  };
  
  const togglePreview = () => {
    setPreviewVisible(!previewVisible);
  };
  
  const downloadPdf = () => {
    if (!pdfUrl) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `${lessonTitle.replace(/\s+/g, '-').toLowerCase()}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  React.useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);
  
  const currentCategory = TEMPLATE_LIBRARY[selectedCategory];
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 flex items-center">
                <Sparkles className="w-8 h-8 mr-3 text-blue-600" />
                LaTeX Template Library
              </h1>
              <p className="text-slate-600 mt-1">Professional templates for every teaching need</p>
            </div>
            <div className="flex items-center space-x-3">
              <button 
                onClick={togglePreview} 
                className="hidden lg:flex items-center px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg shadow-sm hover:bg-slate-50 transition-all"
              >
                {previewVisible ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                {previewVisible ? 'Hide' : 'Show'} Preview
              </button>
              <button 
                onClick={downloadPdf}
                disabled={!pdfUrl}
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg shadow-sm transition-all ${
                  !pdfUrl 
                    ? 'text-slate-400 bg-slate-100 cursor-not-allowed' 
                    : 'text-white bg-blue-600 hover:bg-blue-700'
                }`}
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Panel - Template Selection */}
          <div className={`${previewVisible ? 'lg:w-1/2' : 'w-full'} space-y-6`}>
            
            {/* Category Selection */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Select Category</h2>
              <div className="flex flex-wrap gap-3">
                {Object.entries(TEMPLATE_LIBRARY).map(([key, category]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedCategory(key)}
                    className={`px-6 py-3 rounded-lg border-2 transition-all ${
                      selectedCategory === key
                        ? `border-blue-500 bg-gradient-to-br ${category.color} text-white shadow-lg`
                        : 'border-slate-200 hover:border-slate-300 text-slate-700 hover:shadow-md'
                    }`}
                  >
                    <div className="text-sm font-semibold whitespace-nowrap">{category.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Template Grid */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className={`text-lg font-semibold mb-4 bg-gradient-to-r ${currentCategory.color} bg-clip-text text-transparent`}>
                {currentCategory.name} Templates
              </h2>
              <div className="grid gap-4">
                {currentCategory.templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => selectTemplate(selectedCategory, template)}
                    className={`p-5 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedTemplate?.id === template.id
                        ? `border-blue-500 bg-blue-50 shadow-md`
                        : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                    }`}
                  >
                    <h3 className="font-semibold text-slate-900 mb-1">{template.name}</h3>
                    <p className="text-sm text-slate-600 mb-3">{template.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {template.features.slice(0, 3).map((feature, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded">
                          {feature}
                        </span>
                      ))}
                      {template.features.length > 3 && (
                        <span className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded">
                          +{template.features.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Basic Info */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Lesson Details</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Subject *</label>
                  <input
                    type="text"
                    name="subject"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={lessonParams.subject}
                    onChange={handleInputChange}
                    placeholder="e.g., Mathematics, English"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Grade Level *</label>
                  <select
                    name="gradeLevel"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={lessonParams.gradeLevel}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Grade</option>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(g => (
                      <option key={g} value={`Grade ${g}`}>Grade {g}</option>
                    ))}
                    <option value="University">University</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Topic *</label>
                  <input
                    type="text"
                    name="topic"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={lessonParams.topic}
                    onChange={handleInputChange}
                    placeholder="e.g., Quadratic Functions"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Duration</label>
                  <input
                    type="text"
                    name="duration"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={lessonParams.duration}
                    onChange={handleInputChange}
                    placeholder="e.g., 50 minutes"
                  />
                </div>
              </div>
            </div>

            {/* Custom Instructions */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Custom Instructions</h2>
              <textarea
                name="freeFormInstruction"
                rows={4}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                value={lessonParams.freeFormInstruction}
                onChange={handleInputChange}
                placeholder="Add any special instructions, learning goals, student considerations, or style preferences..."
              />
            </div>

            {/* Homework & Solutions */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Practice & Solutions</h2>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="includeHomework"
                    name="includeHomework"
                    className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                    checked={lessonParams.includeHomework}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="includeHomework" className="text-sm font-medium text-slate-900">
                    Include homework/practice problems
                  </label>
                </div>
                
                {lessonParams.includeHomework && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Number of Problems</label>
                    <input
                      type="number"
                      name="homeworkCount"
                      min="1"
                      max="30"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={lessonParams.homeworkCount}
                      onChange={handleInputChange}
                    />
                  </div>
                )}
                
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="includeSolutions"
                    name="includeSolutions"
                    className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                    checked={lessonParams.includeSolutions}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="includeSolutions" className="text-sm font-medium text-slate-900">
                    Include solutions manual
                  </label>
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={generateLesson}
              disabled={isGenerating || !selectedTemplate}
              className="w-full px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-blue-800 transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isGenerating ? (
                <span className="flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 mr-3 animate-spin" />
                  Generating Your Document...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <Save className="w-5 h-5 mr-3" />
                  Generate from Template
                </span>
              )}
            </button>
          </div>
          
          {/* Right Panel - Preview */}
          {previewVisible && (
            <div className="w-full">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden h-full">
                <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-4">
                  <h2 className="text-xl font-semibold text-white">PDF Preview</h2>
                  <p className="text-slate-300 text-sm mt-1">
                    {selectedTemplate ? `Template: ${selectedTemplate.name}` : 'Select a template to begin'}
                  </p>
                </div>
                
                <div className="p-6 h-full">
                  {isGenerating ? (
                    <div className="flex flex-col items-center justify-center h-96 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg">
                      <div className="relative">
                        <RefreshCw className="w-12 h-12 text-blue-600 animate-spin" />
                        <div className="absolute inset-0 w-12 h-12 border-4 border-blue-200 rounded-full animate-pulse"></div>
                      </div>
                      <p className="text-slate-700 font-medium mt-6">Crafting your beautiful document...</p>
                      <p className="text-slate-500 text-sm mt-2">This may take a few moments</p>
                      <div className="flex space-x-1 mt-4">
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                    </div>
                  ) : pdfUrl ? (
                    <div className="h-full">
                      <iframe 
                        src={pdfUrl} 
                        className="w-full h-full min-h-[600px] rounded-lg shadow-inner"
                        title="PDF Preview"
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-96 bg-gradient-to-br from-slate-50 to-slate-100 border-2 border-dashed border-slate-300 rounded-lg">
                      <div className="text-center px-4">
                        <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center">
                          <Sparkles className="w-8 h-8 text-blue-600" />
                        </div>
                        <p className="text-slate-600 font-medium">Ready to create</p>
                        <p className="text-slate-500 text-sm mt-2">
                          {selectedTemplate 
                            ? `Selected: ${selectedTemplate.name}` 
                            : 'Select a template to get started'}
                        </p>
                        <p className="text-slate-400 text-xs mt-4">
                          Fill in the lesson details and click "Generate from Template"
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Lessons;